import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { createTextureIcon } from "./createTextureIcon";

/**
 * Texture-authored custom icons.
 *
 * These glyphs are NOT in @phosphor-icons/react but plug into the Edges
 * `<Icon name="…" />` component with the same API. Each is authored via
 * `createTextureIcon` (256×256 viewBox, currentColor fill) so it resolves
 * uniformly alongside Phosphor icons.
 *
 * NAMING: every custom icon MUST be prefixed `Texture*`. Phosphor ships ~1,500
 * names and adds more each release; the prefix guarantees a future Phosphor
 * release can never silently shadow a custom icon, and makes it obvious in
 * call sites (e.g. ENTITY_CONFIG) which icons are bespoke. The disjoint-keyset
 * assertion in Icon.tsx enforces this in development.
 *
 * NOTE: the paths below are PLACEHOLDER glyphs (simple, legible stand-ins) so
 * the pattern is wired end-to-end. Replace the `children` with the real
 * Texture-designed SVG paths when they're ready — no other code changes needed.
 */

/**
 * Shared switching-device geometry (256 grid, centered on y=128).
 *
 * The switching family — Switch, Breaker, Recloser, Motor Switch, Sectionalizer
 * — all share one SPST contact base (two terminal poles + leads). Each device
 * swings a blade UP (open) or near-flat across the poles (closed), giving every
 * switchable element a real open/closed geometry, and rides a distinguishing
 * marker on the blade midpoint:
 *   • Switch        — no marker
 *   • Breaker       — outlined square   (the ANSI breaker box)
 *   • Recloser      — outlined circle   (auto-reclose)
 *   • Motor Switch  — outlined diamond
 *   • Sectionalizer — counter dial      (operation counter)
 *
 * STATE = geometry (open vs closed), so the variants are distinct glyphs wired
 * via EntityConfig.states. STATUS COLOR stays a render-layer overlay
 * (GRID_STATE_COLORS) — never baked into these glyphs.
 *
 * Blade midpoints: open ≈ (127,95), closed ≈ (127,125).
 */
const SwitchContactBase = () => (
  <>
    <path d="M12 128h44M200 128h44" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
    <circle cx="76" cy="128" r="20" fill="none" stroke="currentColor" strokeWidth="16" />
    <circle cx="180" cy="128" r="20" fill="none" stroke="currentColor" strokeWidth="16" />
  </>
);
const OpenBlade = () => (
  <path d="M166 120 88 70" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
);
const ClosedBlade = () => (
  <path d="M162 122 92 128" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
);
/**
 * CountDial — the sectionalizer's operation-counter motif: a near-complete ring
 * (gap at the bottom, opening toward the switch) with a filled center hub. Reads
 * as a rotary counter/dial. Fixed to the body, so it stays put across open/closed
 * while only the blade swings. Centered at (128,58), r=32 on the 256 grid.
 */
const CountDial = () => (
  <>
    <path
      d="M112 85.7 A32 32 0 1 1 144 85.7"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
    />
    <circle cx="128" cy="58" r="8" fill="currentColor" stroke="none" />
  </>
);

/**
 * Recloser — a bold "R" inside a square. The letter is a real typeface glyph
 * (SVG <text>) so it reads cleanly at any size. State (open/closed) is NOT
 * expressed geometrically here — the letter-badge is a stable identity mark;
 * live open/closed status is conveyed by the render-layer color overlay.
 */
const TextureRecloser = createTextureIcon(
  "TextureRecloser",
  <>
    <rect x="36" y="36" width="184" height="184" fill="none" stroke="currentColor" strokeWidth="16" />
    <text
      x="128"
      y="126"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
      fontSize="140"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
    >
      R
    </text>
  </>
);

/**
 * Fuse — a single stroked sine cycle (one up/down bump) on a horizontal lead,
 * extending flat to both edges. Starts from Phosphor's WaveSine motif but is a
 * thin stroked line (not the filled glyph) with leads, matching the schematic
 * fuse symbol. Authored on the 256 grid, centered on y=128.
 */
const TextureFuse = createTextureIcon(
  "TextureFuse",
  <path
    d="M20 128h72q18 -84 36 0q18 84 36 0h72"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
);

/**
 * Breaker — a bold "B" inside a square. The letter is a real typeface glyph
 * (SVG <text>) so it reads cleanly at any size. State (open/closed) is NOT
 * expressed geometrically here — the letter-badge is a stable identity mark;
 * live open/closed status is conveyed by the render-layer color overlay.
 */
const TextureBreaker = createTextureIcon(
  "TextureBreaker",
  <>
    <rect x="36" y="36" width="184" height="184" fill="none" stroke="currentColor" strokeWidth="16" />
    <text
      x="128"
      y="126"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
      fontSize="140"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
    >
      B
    </text>
  </>
);

/**
 * Source — the standard AC source symbol: a single stroked sine cycle (matching
 * the Fuse wave, minus the leads) centered inside a circle, with short
 * horizontal leads extending to each side.
 */
const TextureSource = createTextureIcon(
  "TextureSource",
  <>
    <path d="M4 128h40M212 128h40" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
    <circle cx="128" cy="128" r="84" fill="none" stroke="currentColor" strokeWidth="16" />
    <path
      d="M92 128q18 -84 36 0q18 84 36 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </>
);

/**
 * Capacitor — the standard schematic symbol: two parallel vertical plates in
 * the center with horizontal leads extending to each side.
 */
const TextureCapacitor = createTextureIcon(
  "TextureCapacitor",
  <>
    <path
      d="M4 128h106M146 128h106M110 72v112M146 72v112"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
    />
  </>
);

/**
 * Regulator — a bold "V" inside a circle (mirrors the Recloser treatment, which
 * is an "R" in a square). The letter is a real typeface glyph (SVG <text>) so it
 * reads cleanly at any size.
 */
const TextureRegulator = createTextureIcon(
  "TextureRegulator",
  <>
    <circle cx="128" cy="128" r="92" fill="none" stroke="currentColor" strokeWidth="16" />
    <text
      x="128"
      y="126"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
      fontSize="128"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
    >
      V
    </text>
  </>
);

/**
 * Switch — SPST contact, no marker. Open swings the blade up; closed lays it
 * near-flat across the poles. Centered on y=128 via the shared base.
 */
const TextureSwitch = createTextureIcon(
  "TextureSwitch",
  <>
    <SwitchContactBase />
    <OpenBlade />
  </>
);

const TextureSwitchClosed = createTextureIcon(
  "TextureSwitchClosed",
  <>
    <SwitchContactBase />
    <ClosedBlade />
  </>
);

/**
 * Open Point — a normally-open decision/tie point, drawn as two facing brackets
 * "( )" that cup the conductor with an open gap between them, matching the
 * utility GIS symbol. Heavier stroke than a text bracket; openings face inward,
 * bellies point outward. Rendered transparent over the line (no chip) by the map.
 */
const TextureOpenPoint = createTextureIcon(
  "TextureOpenPoint",
  <>
    <path
      d="M112 52 C 74 92, 74 164, 112 204"
      fill="none"
      stroke="currentColor"
      strokeWidth="18"
      strokeLinecap="round"
    />
    <path
      d="M144 52 C 182 92, 182 164, 144 204"
      fill="none"
      stroke="currentColor"
      strokeWidth="18"
      strokeLinecap="round"
    />
  </>
);

/**
 * Sectionalizer — switch with an operation-counter dial. A sectionalizer counts
 * upstream recloser operations and opens during a dead interval, so the counter
 * dial is its identity mark. The dial is fixed to the body (stays put across
 * open/closed); only the blade swings.
 */
const TextureSectionalizerOpen = createTextureIcon(
  "TextureSectionalizerOpen",
  <>
    <SwitchContactBase />
    <OpenBlade />
    <CountDial />
  </>
);
const TextureSectionalizerClosed = createTextureIcon(
  "TextureSectionalizerClosed",
  <>
    <SwitchContactBase />
    <ClosedBlade />
    <CountDial />
  </>
);

/**
 * Transformer — the standard schematic symbol: two facing windings (coils),
 * each drawn as a vertical stack of three semicircular bumps with horizontal
 * leads at top and bottom. Left coil bulges right, right coil bulges left, so
 * the two windings face each other across the core gap. Authored on the 256
 * grid; coils span y=64→196 (3 bumps, r=22).
 */
const TextureTransformer = createTextureIcon(
  "TextureTransformer",
  <>
    {/* left winding — bumps bulge right, leads exit left */}
    <path
      d="M40 64H88A22 22 0 0 1 88 108A22 22 0 0 1 88 152A22 22 0 0 1 88 196H40"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* right winding — bumps bulge left, leads exit right */}
    <path
      d="M216 64H168A22 22 0 0 0 168 108A22 22 0 0 0 168 152A22 22 0 0 0 168 196H216"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </>
);

/**
 * Phase × medium line matrix. Lines are drawn as N parallel horizontal
 * conductors (1/2/3 = single/two/three-phase), SOLID for overhead and DASHED
 * for underground (buried / not visible). Authored on the 256 grid; the
 * conductor stack is vertically centered on y=128 with 36u spacing. The dash
 * uses pathLength=100 + "20 20" so it tiles evenly with no clipped end dash.
 */
const PHASE_YS = { 1: [128], 2: [110, 146], 3: [92, 128, 164] } as const;

const solidLine = (y: number) => (
  <path key={y} d={`M24 ${y}h208`} fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
);
const dashedLine = (y: number) => (
  <path
    key={y}
    d={`M24 ${y}h208`}
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="round"
    pathLength={100}
    strokeDasharray="20 20"
  />
);

const TextureSinglePhaseOverheadLine = createTextureIcon(
  "TextureSinglePhaseOverheadLine",
  <>{PHASE_YS[1].map(solidLine)}</>
);
const TextureTwoPhaseOverheadLine = createTextureIcon("TextureTwoPhaseOverheadLine", <>{PHASE_YS[2].map(solidLine)}</>);
const TextureThreePhaseOverheadLine = createTextureIcon(
  "TextureThreePhaseOverheadLine",
  <>{PHASE_YS[3].map(solidLine)}</>
);
const TextureSinglePhaseUndergroundLine = createTextureIcon(
  "TextureSinglePhaseUndergroundLine",
  <>{PHASE_YS[1].map(dashedLine)}</>
);
const TextureTwoPhaseUndergroundLine = createTextureIcon(
  "TextureTwoPhaseUndergroundLine",
  <>{PHASE_YS[2].map(dashedLine)}</>
);
const TextureThreePhaseUndergroundLine = createTextureIcon(
  "TextureThreePhaseUndergroundLine",
  <>{PHASE_YS[3].map(dashedLine)}</>
);

/**
 * Support Structure — a single bold filled dot, larger than Phosphor's Dot, to
 * mark a pole / support point on the network. Centered on the 256 grid.
 */
const TextureSupportStructure = createTextureIcon(
  "TextureSupportStructure",
  <circle cx="128" cy="128" r="40" fill="currentColor" stroke="none" />
);

// Match Phosphor regular: 16 units on a 256-unit grid (1.5px at 24px).
const TextureExplore = createTextureIcon(
  "TextureExplore",
  <g
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  >
    <circle cx="128" cy="128" r="96" />
    <path d="M170.667 85.333 149.333 149.333 85.333 170.667 106.667 106.667Z M106.667 106.667 149.333 149.333" />
  </g>
);

const TextureBoards = createTextureIcon(
  "TextureBoards",
  <path
    d="M32 21.333H106.667V128H32Z M149.333 21.333H224V85.333H149.333Z M32 170.667H106.667V234.667H32Z M149.333 128H224V234.667H149.333Z"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureMonitors = createTextureIcon(
  "TextureMonitors",
  <path
    d="M32 74.667H224 M42.667 202.667H106.667L170.667 117.333H224"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureSurvey = createTextureIcon(
  "TextureSurvey",
  <path
    d="M 32 74.667 V 32 H 74.667 M 181.333 32 H 224 V 74.667 M 224 181.333 V 224 H 181.333 M 74.667 224 H 32 V 181.333 M 74.667 128 H 96 M 160 128 H 181.333 M 128 74.667 V 96 M 128 160 V 181.333 M 96 128 A 32 32 0 1 0 160 128 A 32 32 0 1 0 96 128"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureSeismic = createTextureIcon(
  "TextureSeismic",
  <path
    d="M 32 55.36 H 224 M 21.333 162.027 H 53.333 L 74.667 119.36 L 96 204.693 L 117.333 98.027 L 138.667 183.36 L 160 140.693 L 181.333 162.027 H 234.667"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureWaypoint = createTextureIcon(
  "TextureWaypoint",
  <path
    d="M 17.067 180.267 L 49.067 148.267 L 81.067 180.267 L 49.067 212.267 Z M 81.067 180.267 H 102.4 L 166.4 116.267 M 155.733 41.6 H 134.4 C 91.733 41.6 91.733 105.6 134.4 105.6 H 155.733 M 198.4 41.6 H 209.067 C 251.733 41.6 251.733 105.6 209.067 105.6 H 198.4 M 145.067 73.6 H 198.4"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureTerrain = createTextureIcon(
  "TextureTerrain",
  <path
    d="M 32 32 H 224 V 224 H 32 Z M 32 128 H 224 M 128 32 V 224 M 32 128 L 128 32 M 128 224 L 224 128"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureMagma = createTextureIcon(
  "TextureMagma",
  <path
    d="M 32 226.667 V 162.667 C 32 88 128 130.667 128 56 V 34.667 H 224 M 85.333 226.667 V 194.667 C 85.333 130.667 181.333 173.333 181.333 98.667 V 88 H 224 M 138.667 226.667 C 138.667 184 224 194.667 224 141.333"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

const TextureGround = createTextureIcon(
  "TextureGround",
  <path
    d="M 32 80 L 128 32 L 224 80 V 176 L 128 224 L 32 176 Z M 32 80 L 85.333 106.667 M 170.667 106.667 L 224 80 M 128 170.667 V 224 M 85.333 106.667 L 128 85.333 L 170.667 106.667 V 149.333 L 128 170.667 L 85.333 149.333 Z M 85.333 106.667 L 128 128 L 170.667 106.667 M 128 128 V 170.667"
    fill="none"
    stroke="currentColor"
    strokeWidth="16"
    strokeLinecap="butt"
    strokeLinejoin="miter"
    strokeMiterlimit="2"
  />
);

/** A measured point framed by four open corners. The larger center stays legible at 16px. */
const TextureSensor = createTextureIcon(
  "TextureSensor",
  <>
    <path
      d="M88 40H40v48M168 40h48v48M216 168v48h-48M88 216H40v-48"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="128" cy="128" r="36" fill="currentColor" stroke="none" />
  </>
);

/**
 * Registry of all custom icons, keyed by name. The Edges `Icon` resolver checks
 * this map first, then falls back to Phosphor. `as const satisfies` keeps the
 * keys as a literal union (drives `CustomIconName` + autocomplete) while
 * type-checking each value is a Phosphor-compatible component.
 */
export const CUSTOM_ICON_REGISTRY = {
  TextureSensor,
  TextureSurvey,
  TextureSeismic,
  TextureWaypoint,
  TextureTerrain,
  TextureMagma,
  TextureGround,

  TextureExplore,
  TextureBoards,
  TextureMonitors,
  TextureRecloser,
  TextureFuse,
  TextureBreaker,
  TextureSource,
  TextureCapacitor,
  TextureRegulator,
  TextureSwitch,
  TextureSwitchClosed,
  TextureOpenPoint,
  TextureSectionalizerOpen,
  TextureSectionalizerClosed,
  TextureTransformer,
  TextureSinglePhaseOverheadLine,
  TextureTwoPhaseOverheadLine,
  TextureThreePhaseOverheadLine,
  TextureSinglePhaseUndergroundLine,
  TextureTwoPhaseUndergroundLine,
  TextureThreePhaseUndergroundLine,
  TextureSupportStructure,
} as const satisfies Record<string, PhosphorIcon>;

/** Union of all custom icon names. Folded into `IconName`. */
export type CustomIconName = keyof typeof CUSTOM_ICON_REGISTRY;
